import api from '../../axios';
import { handleApiError } from '../../errorHandler';
import {
  ICustomer,
  ICreateCustomerRequest,
  IUpdateCustomerRequest,
  IUpdateCustomerCompleteRequest,
  IUpdateCustomerSelfRequest,
  IPerformanceHistory,
} from './types';

/**
 * Services for Customers module
 */

// Get all customers
export const getCustomers = async (params?: {
  includeEngagement?: boolean;
}): Promise<ICustomer[]> => {
  try {
    const response = await api.get('/customers', { params });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clientes:', error);
    throw handleApiError(error);
  }
};

// Get customers based on authenticated user profile and relationships
export const getMyCustomers = async (): Promise<ICustomer[]> => {
  try {
    const response = await api.get('/customers/me');
    return response.data;
  } catch (error) {
    console.error('Detailed error when fetching my customers:', error);
    throw handleApiError(error);
  }
};

/**
 * Postos com vínculo SalesPetro (mainUser ou equipe), independente do perfil.
 * Preferível ao /customers/me para o seletor multi-posto (admin vinculado a postos
 * não deve receber a lista completa do sistema).
 */
export const getAccessibleStations = async (): Promise<ICustomer[]> => {
  try {
    const response = await api.get('/customers/accessible');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar postos acessíveis:', error);
    throw handleApiError(error);
  }
};

export const setMyDefaultCustomer = async (
  customerId: string
): Promise<{ defaultCustomerId: string }> => {
  try {
    const response = await api.patch<{ defaultCustomerId: string }>(
      '/customers/me/default-customer',
      { customerId }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao definir posto padrão:', error);
    throw handleApiError(error);
  }
};

// Get a specific customer by ID
export const getCustomer = async (
  id: string,
  params?: { includeEngagement?: boolean }
): Promise<ICustomer> => {
  try {
    const response = await api.get(`/customers/${id}`, { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Create a new customer
export const createCustomer = async (customer: ICreateCustomerRequest): Promise<ICustomer> => {
  try {
    // Construir payload base
    const payload: any = {
      personId: customer.personId || (customer as any).people?.personId,
      categoryId: customer.categoryId,
      subscriptionPlan: customer.subscriptionPlan || 'legacy',
      activatedAt: customer.activatedAt || null,
      userIds: Array.isArray(customer.userIds) ? customer.userIds : [],
      operationalBasesIds: Array.isArray(customer.operationalBasesIds)
        ? customer.operationalBasesIds
        : [],
      relationshipIds: Array.isArray(customer.relationshipIds) ? customer.relationshipIds : [],
    };

    // ✅ anpCode: SEMPRE incluir, mesmo se vazio
    payload.anpCode =
      customer.anpCode !== undefined && customer.anpCode !== null
        ? String(customer.anpCode || '')
        : '';

    // ✅ brandId: SEMPRE incluir se fornecido, ou null para categorias não-Posto
    let brandId = (customer as any).brandId || (customer as any).brand?.id;

    if (brandId && typeof brandId === 'string' && brandId.trim() !== '') {
      // Validar se é UUID válido
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(brandId.trim())) {
        payload.brandId = brandId.trim();
      } else {
        // Se não for UUID válido, usar null
        payload.brandId = null;
      }
    } else {
      // Se não fornecido, usar null (backend pode aceitar null para categorias não-Posto)
      payload.brandId = null;
    }

    // Adicionar mainUserId apenas se estiver preenchido
    if (
      customer.mainUserId &&
      typeof customer.mainUserId === 'string' &&
      customer.mainUserId.trim() !== ''
    ) {
      payload.mainUserId = customer.mainUserId.trim();
    }

    // IMPORTANTE: Remover people se existir - o backend espera apenas personId no nível raiz
    if ((customer as any).people) {
      delete (customer as any).people;
    }

    // Remover id se existir (não deve ser enviado na criação)
    if (payload.id !== undefined) {
      delete payload.id;
    }

    // Adicionar performanceHistory se fornecido
    if ((customer as any).performanceHistory) {
      payload.performanceHistory = (customer as any).performanceHistory;
    }

    const response = await api.post('/customers', payload);
    return response.data;
  } catch (error: any) {
    console.error('❌ Erro ao criar cliente:', error);
    console.error('Erro detalhado:', error.response?.data);
    console.error('Status do erro:', error.response?.status);
    throw handleApiError(error);
  }
};

// Register a new customer (public endpoint)
export const registerCustomer = async (customerData: any): Promise<ICustomer> => {
  try {
    const response = await api.post('/customers/register', customerData);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao registrar cliente:', error);

    if (error.response) {
      throw new Error(
        `Erro ao registrar cliente: ${error.response.data?.message || 'Erro de validação'}`
      );
    }

    throw handleApiError(error);
  }
};

// Update an existing customer (PATCH /customers/:id — UpdateCustomerDto, campos opcionais)
export const updateCustomer = async (
  id: string,
  customer: IUpdateCustomerRequest
): Promise<ICustomer> => {
  try {
    const sanitizedData: any = { ...customer };

    // Só incluir arrays quando o caller enviou — evita zerar vínculos/bases em PATCH parcial
    if ('userIds' in customer) {
      sanitizedData.userIds = Array.isArray(customer.userIds) ? customer.userIds : [];
    } else {
      delete sanitizedData.userIds;
    }
    if ('operationalBasesIds' in customer) {
      sanitizedData.operationalBasesIds = Array.isArray(customer.operationalBasesIds)
        ? customer.operationalBasesIds
        : [];
    } else {
      delete sanitizedData.operationalBasesIds;
    }
    if ('relationshipIds' in customer) {
      sanitizedData.relationshipIds = Array.isArray(customer.relationshipIds)
        ? customer.relationshipIds
        : [];
    } else {
      delete sanitizedData.relationshipIds;
    }

    // brandId: omitir se vazio/inválido (DTO exige UUID quando presente)
    if (
      sanitizedData.brandId === null ||
      sanitizedData.brandId === undefined ||
      sanitizedData.brandId === ''
    ) {
      delete sanitizedData.brandId;
    } else if (typeof sanitizedData.brandId === 'string') {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(sanitizedData.brandId.trim())) {
        delete sanitizedData.brandId;
      } else {
        sanitizedData.brandId = sanitizedData.brandId.trim();
      }
    }

    if (sanitizedData.anpCode !== undefined && sanitizedData.anpCode !== null) {
      sanitizedData.anpCode = String(sanitizedData.anpCode);
    } else {
      delete sanitizedData.anpCode;
    }

    const response = await api.patch(`/customers/${id}`, sanitizedData);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao atualizar cliente:', error);
    console.error('Erro detalhado:', error.response?.data);
    console.error('Status do erro:', error.response?.status);
    throw handleApiError(error);
  }
};

// Update customer with performance history
export const updateCustomerWithPerformance = async (
  id: string,
  customer: IUpdateCustomerRequest,
  performanceData?: {
    referenceDate: string;
    salesVolume?: number;
    averageRevenue?: number;
    convenienceRevenue?: number;
    oilChangeRevenue?: number;
    fuelPumpRevenue?: number;
  }
): Promise<ICustomer> => {
  try {
    // Preparar os dados do cliente sem campos undefined
    const customerDataClean = customer;

    // Limpar campos undefined
    const cleanCustomerData = Object.fromEntries(
      Object.entries(customerDataClean).filter(([key, value]) => value !== undefined)
    );

    const sanitizedData = {
      ...cleanCustomerData,
      userIds: Array.isArray(customer.userIds) ? customer.userIds : [],
      operationalBasesIds: Array.isArray(customer.operationalBasesIds)
        ? customer.operationalBasesIds
        : [],
      relationshipIds: Array.isArray(customer.relationshipIds) ? customer.relationshipIds : [],
      // Incluir performanceHistory se fornecido
      ...(performanceData && { performanceHistory: performanceData }),
    };

    const response = await api.patch(`/customers/${id}`, sanitizedData);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao atualizar cliente com performance:', error);
    // Usar handleApiError que já faz toda a tradução
    throw handleApiError(error);
  }
};

// Delete a customer
export const deleteCustomer = async (id: string): Promise<void> => {
  try {
    await api.delete(`/customers/${id}`);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Get customer performance history
export const getCustomerPerformanceHistory = async (
  customerId: string
): Promise<IPerformanceHistory[]> => {
  try {
    const response = await api.get(`/customers/${customerId}/performance-history`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico de performance:', error);
    throw handleApiError(error);
  }
};

// Get latest customer performance history
export const getLatestCustomerPerformanceHistory = async (
  customerId: string
): Promise<IPerformanceHistory | null> => {
  try {
    const response = await api.get(`/customers/${customerId}/performance-history/latest`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar último histórico de performance:', error);
    throw handleApiError(error);
  }
};

// Get customer performance history by date range
export const getCustomerPerformanceHistoryByDateRange = async (
  customerId: string,
  startDate: string,
  endDate: string
): Promise<IPerformanceHistory[]> => {
  try {
    const response = await api.get(`/customers/${customerId}/performance-history`, {
      params: { startDate, endDate },
    });
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar histórico de performance por período:', error);
    throw handleApiError(error);
  }
};

// Get available categories
export const getCategories = async () => {
  try {
    const response = await api.get('/customer-categories');
    return response.data;
  } catch (error) {
    console.error('Error loading categories:', error);
    throw handleApiError(error);
  }
};

// Get available brands
export const getBrands = async () => {
  try {
    const response = await api.get('/brands');
    return response.data;
  } catch (error) {
    console.error('Error loading brands:', error);
    throw handleApiError(error);
  }
};

// Get performance history by customer and date
export const getPerformanceHistoryByDate = async (
  customerId: string,
  referenceDate: string
): Promise<IPerformanceHistory | null> => {
  try {
    const response = await api.get(`/customers/${customerId}/performance-history`, {
      params: { referenceDate },
    });

    // O backend retorna um array, mas queremos apenas o primeiro item se existir
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data[0];
    }

    return null;
  } catch (error: any) {
    console.error('Erro ao buscar histórico por data:', error);

    // Se não encontrar (404), retorna null em vez de erro
    if (error.response?.status === 404) {
      return null;
    }

    throw handleApiError(error);
  }
};

// Delete performance history record
export const deletePerformanceHistory = async (
  customerId: string,
  filter: { id?: string; referenceDate?: string }
): Promise<void> => {
  try {
    await api.delete(`/customers/${customerId}/performance-history`, {
      data: filter,
    });
  } catch (error: any) {
    throw handleApiError(error);
  }
};

const CUSTOMER_SELF_ALLOWED_KEYS = [
  'anpCode',
  'brandId',
  'categoryId',
  'operationalBasesIds',
  'myStationPurchaseDeliveryMode',
  'people',
  'mainUser',
  'performanceHistory',
] as const;

/** Remove campos fora do DTO de PATCH /customers/self. */
export function sanitizeCustomerSelfPayload(
  data: Record<string, unknown>
): IUpdateCustomerSelfRequest {
  const payload: Record<string, unknown> = {};
  CUSTOMER_SELF_ALLOWED_KEYS.forEach((key) => {
    if (data[key] !== undefined) {
      payload[key] = data[key];
    }
  });
  return payload as IUpdateCustomerSelfRequest;
}

/** Auto-atualização do cliente autenticado (não usar rotas admin). */
export const updateCustomerSelf = async (
  customerData: IUpdateCustomerSelfRequest | Record<string, unknown>
): Promise<ICustomer> => {
  const payload = sanitizeCustomerSelfPayload(customerData as Record<string, unknown>);

  if (process.env.NODE_ENV !== 'production') {
    console.info('[customers/self] PATCH payload:', payload);
  }

  try {
    const response = await api.patch('/customers/self', payload);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar cliente (self):', error, payload);
    throw handleApiError(error);
  }
};

// Nova função para atualizar cliente usando o endpoint completo (admin)
export const updateCustomerComplete = async (
  id: string,
  customerData: IUpdateCustomerCompleteRequest
): Promise<ICustomer> => {
  try {
    // Construir payload base
    const payload: any = { ...customerData };

    // Campos aceitos só no PATCH básico (/customers/:id) — não enviar no complete
    delete payload.activatedAt;
    delete payload.userIds;
    delete payload.type;
    delete payload.document;
    delete payload.mainUserId;
    delete payload.personId;

    // ✅ anpCode: SEMPRE incluir, mesmo se vazio
    if (payload.anpCode === undefined || payload.anpCode === null) {
      payload.anpCode = '';
    } else {
      payload.anpCode = String(payload.anpCode);
    }

    // ✅ operationalBasesIds: Sempre incluir se fornecido (garantir que seja array válido)
    if ('operationalBasesIds' in payload && payload.operationalBasesIds !== undefined) {
      payload.operationalBasesIds = Array.isArray(payload.operationalBasesIds)
        ? payload.operationalBasesIds
        : [];
    }

    // ✅ brandId: Garantir que está presente
    // Se não estiver presente, tentar buscar de múltiplas fontes ou usar null
    if (!payload.brandId) {
      // Tentar buscar do customerData original
      const brandIdFromData = (customerData as any).brandId || (customerData as any).brand?.id;

      if (brandIdFromData && typeof brandIdFromData === 'string' && brandIdFromData.trim() !== '') {
        // Validar se é UUID válido
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(brandIdFromData.trim())) {
          payload.brandId = brandIdFromData.trim();
        } else {
          payload.brandId = null;
        }
      } else {
        // Se não encontrou brandId, usar null
        // ✅ Backend corrigido: agora aceita brandId: null para categorias não-Posto
        payload.brandId = null;
      }
    } else {
      // Se brandId está presente, garantir que seja string válida ou null
      if (typeof payload.brandId === 'string' && payload.brandId.trim() === '') {
        payload.brandId = null;
      }
    }

    // Remover subscriptionPlan se for string vazia (opcional)
    if (payload.subscriptionPlan === '') {
      delete payload.subscriptionPlan;
    }

    // ✅ operationalBasesIds: Sempre incluir se fornecido
    if ('operationalBasesIds' in payload) {
      payload.operationalBasesIds = Array.isArray(payload.operationalBasesIds)
        ? payload.operationalBasesIds
        : [];
    }

    // Remover relationshipIds apenas se array vazio (opcional)
    if (Array.isArray(payload.relationshipIds) && payload.relationshipIds.length === 0) {
      delete payload.relationshipIds;
    }

    const response = await api.patch(`/customers/update/${id}`, payload);
    return response.data;
  } catch (error: any) {
    console.error('❌ Erro ao atualizar cliente (endpoint completo):', error);
    console.error('Erro detalhado:', error.response?.data);
    console.error('Status do erro:', error.response?.status);
    throw handleApiError(error);
  }
};

export const cancelCustomerBilling = async (customerId: string): Promise<void> => {
  try {
    await api.post(`/customers/${customerId}/billing/cancel`);
  } catch (error) {
    console.error('Erro ao cancelar assinatura do cliente:', error);
    throw handleApiError(error);
  }
};
